import { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCurrentConferenceShortName,
  getErrorMessage,
  getConferenceLoginDetails,
  getConferenceLoginDetailsByUrl,
  getImportantDetailsByShortName
} from '@/services/api';
import { toast } from 'sonner';

const ConferenceContext = createContext(null);

export const useConference = () => {
  const context = useContext(ConferenceContext);
  if (!context) {
    throw new Error('useConference must be used within ConferenceProvider');
  }
  return context;
};

export const ConferenceProvider = ({ children }) => {
  const [siteConfig, setSiteConfig] = useState(getCachedSiteConfig());
  const [loginDetails, setLoginDetails] = useState(getCachedLoginDetails());
  const [importantDetails, setImportantDetails] = useState(getCachedImportantDetails() || getDefaultImportantDetails());
  const [pricing, setPricing] = useState(null);
  const [shortName, setShortName] = useState(getCurrentConferenceShortName());
  const [loading, setLoading] = useState(false); // Start with false for immediate loading
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch conference login details from external API
  const fetchLoginDetails = async (conferenceUrl = null) => {
    try {
      let data;
      if (conferenceUrl) {
        // Submit URL to get login details
        data = await getConferenceLoginDetailsByUrl(conferenceUrl);
      } else {
        // Use current hostname
        data = await getConferenceLoginDetails();
      }
      
      console.log('Login API raw response:', data);
      
      // Data might be an array or object
      const details = Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data;
      console.log('Parsed login details:', details);
      
      setLoginDetails(details);
      // Cache in localStorage
      if (details) {
        localStorage.setItem('conferenceLoginDetails', JSON.stringify(details));
      }
      console.log('Loaded login details:', details);
      
      // Update shortName from login details username
      if (details && details.username) {
        setShortName(details.username);
        console.log('Will fetch important details for username:', details.username);
        await fetchImportantDetails(details.username);
      } else {
        console.log('No username in login details, skipping important details fetch');
      }
      
      return details;
    } catch (err) {
      console.error('Failed to fetch login details:', err);
      // Don't set error for login details, it's optional
      return null;
    }
  };

  // Fetch important conference details using shortname
  const fetchImportantDetails = async (shortName) => {
    console.log('Fetching important details for shortName:', shortName);
    try {
      const data = await getImportantDetailsByShortName(shortName);
      // Data might be an array or object
      const details = Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data;
      const finalDetails = details || getDefaultImportantDetails();
      setImportantDetails(finalDetails);
      // Cache in localStorage
      localStorage.setItem('conferenceImportantDetails', JSON.stringify(finalDetails));
      console.log('Loaded important details:', finalDetails);
      return finalDetails;
    } catch (err) {
      console.error('Failed to fetch important details:', err.message, err.response?.data);
      // Don't set error for important details, use default instead
      setImportantDetails(getDefaultImportantDetails());
      return getDefaultImportantDetails();
    }
  };

  // Fetch pricing for all categories
  const fetchPricing = async (conferenceId) => {
    // Use fallback pricing since API doesn't exist
    const fallbackPricing = {
      speaker: getFallbackPricing('speaker'),
      delegate: getFallbackPricing('delegate'),
      poster: getFallbackPricing('poster'),
      student: getFallbackPricing('student'),
    };
    setPricing(fallbackPricing);
    return fallbackPricing;
  };

  // Submit conference URL to get login and important details
  const submitConferenceUrl = async (conferenceUrl) => {
    setLoading(true);
    setError(null);
    
    try {
      const loginData = await fetchLoginDetails(conferenceUrl);
      if (loginData && loginData.username) {
        // Important details are already fetched in fetchLoginDetails
        return { loginDetails: loginData, importantDetails };
      }
      return { loginDetails: loginData, importantDetails: null };
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      console.error('Failed to submit conference URL:', err);
      toast.error(`Failed to load conference details: ${errorMsg}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Refresh conference data
  const refreshSiteConfig = () => {
    // Use default config since API doesn't exist
    setSiteConfig(getDefaultConfig(shortName));
    return getDefaultConfig(shortName);
  };

  // Change conference by short name
  const changeConference = async (newShortName) => {
    if (newShortName === shortName) return;
    
    setShortName(newShortName);
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchLoginDetails(),
        fetchPricing(newShortName)
      ]);
      console.log('Changed conference to:', newShortName);
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      console.error('Failed to change conference:', err);
      toast.error(`Failed to change conference: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Get pricing for specific category based on current tier
  const getPricing = (category = 'delegate') => {
    if (pricing && pricing[category]) {
      return pricing[category].prices ? pricing[category].prices[category] : pricing[category];
    }
    
    // Fallback to hardcoded pricing if API fails
    const tier = siteConfig?.pricingTier?.tier || 'standard';
    const pricingConfig = {
      speaker: {
        earlyBird: 649,
        midTerm: 649,
        late: 649,
        onSpot: 649,
      },
      delegate: {
        earlyBird: 749,
        midTerm: 749,
        late: 749,
        onSpot: 749,
      },
      listener: {
        earlyBird: 749,
        midTerm: 749,
        late: 749,
        onSpot: 749,
      },
      poster: {
        earlyBird: 499,
        midTerm: 499,
        late: 499,
        onSpot: 499,
      },
      student: {
        earlyBird: 349,
        midTerm: 349,
        late: 349,
        onSpot: 349,
      },
      exhibitor: {
        earlyBird: 5000,
        midTerm: 5000,
        late: 5000,
        onSpot: 5000,
      },
    };

    return pricingConfig[category]?.[tier] || 0;
  };

  // Get all pricing
  const getAllPricing = () => {
    return {
      speaker: getPricing('speaker'),
      delegate: getPricing('delegate'),
      listener: getPricing('listener'),
      poster: getPricing('poster'),
      student: getPricing('student'),
      exhibitor: getPricing('exhibitor'),
    };
  };

  // Check if registration is open
  const isRegistrationOpen = () => {
    return siteConfig?.registrationOpen !== false;
  };

  // Check if abstract submission is open
  const isAbstractSubmissionOpen = () => {
    return siteConfig?.abstractSubmissionOpen !== false;
  };

  // Get days until conference
  const getDaysUntilConference = () => {
    if (!siteConfig?.dates?.conferenceDates) return null;
    
    try {
      const confDateStr = siteConfig.dates.conferenceDates.split(' to ')[0] || siteConfig.dates.conferenceDates;
      const confDate = new Date(confDateStr);
      const today = new Date();
      const diff = confDate - today;
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    } catch (err) {
      console.error('Error calculating days until conference:', err);
      return null;
    }
  };

  // Get pricing tier label
  const getPricingTierLabel = () => {
    return siteConfig?.pricingTier?.label || 'Standard';
  };

  // Get pricing tier color
  const getPricingTierColor = () => {
    return siteConfig?.pricingTier?.color || 'yellow';
  };

  // Get current pricing tier
  const getPricingTier = () => {
    // If dates are null or empty, default to earlybird
    const hasDates = importantDetails?.ConferenceDates || siteConfig?.dates?.start;
    if (!hasDates) {
      return 'earlyBird';
    }
    return siteConfig?.pricingTier?.tier || 'standard';
  };

  // Get conference name
  const getConferenceName = () => {
    return importantDetails?.ConferenceTitle || loginDetails?.ConfName || siteConfig?.fullName || siteConfig?.title || 'Conference';
  };

  // Get conference short name
  const getConferenceShortName = () => {
    return loginDetails?.username || importantDetails?.ShortName || siteConfig?.shortName || shortName;
  };

  // Get conference theme/tagline
  const getConferenceTheme = () => {
    return siteConfig?.theme || siteConfig?.tagline || '';
  };

  // Get conference venue from important details API
  const getConferenceVenue = () => {
    return importantDetails?.ConferenceVenue || siteConfig?.venue?.name || 'Conference Venue';
  };

  // Get contact information
  const getContact = () => {
    return siteConfig?.contact || {};
  };

  // Get social media links
  const getSocial = () => {
    return siteConfig?.social || {};
  };

  // Get conference logo
  const getLogo = () => {
    return siteConfig?.branding?.logo || '';
  };

  // Get conference dates
  const getDates = () => {
    return siteConfig?.dates || {};
  };

  // Get deadlines
  const getDeadlines = () => {
    const dates = getDates();
    return {
      abstractSubmission: dates.abstractSubmissionDeadline,
      earlyBird: dates.earlyBirdDeadline,
      midTerm: dates.midTermDeadline,
      late: dates.lateRegistrationDeadline,
      onSpot: dates.onSpotDate,
    };
  };

  // Get important dates from API
  const getImportantDates = () => {
    return {
      abstract_submission_deadline: importantDetails?.abstract_submission_deadline || '',
      registration_opens: importantDetails?.registration_opens || '',
      EarlyBird: importantDetails?.EarlyBird || '',
      mid_term: importantDetails?.mid_term || '',
      OnSpot: importantDetails?.OnSpot || '',
    };
  };

  // Load site configuration and login details on mount
  useEffect(() => {
    console.log('ConferenceContext useEffect triggered, shortName:', shortName);

    // Set default config immediately to prevent loading delay
    if (!siteConfig) {
      setSiteConfig(getDefaultConfig(shortName));
    }
    setLoading(false); // Set loading to false immediately

    // Load all API data immediately in parallel for fast loading
    const loadAllData = async () => {
      try {
        console.log('Loading all conference data in parallel...');
        // Fetch everything in parallel for maximum speed
        await Promise.all([
          fetchLoginDetails(),
          fetchPricing(shortName)
        ]);
        console.log('All conference data loaded successfully');
      } catch (error) {
        console.error('Conference data loading failed:', error);
        // Don't show error toast, use cached/default data
      }
    };

    // Start loading immediately
    loadAllData();
  }, []);

  const value = {
    // State
    siteConfig,
    loginDetails,
    importantDetails,
    conference: importantDetails, // Alias for backward compatibility
    pricing,
    shortName,
    loading,
    backgroundLoading,
    error,
    
    // Actions
    fetchLoginDetails,
    fetchImportantDetails,
    fetchPricing,
    submitConferenceUrl,
    changeConference,
    refreshSiteConfig,
    
    // Pricing
    getPricing,
    getAllPricing,
    getPricingTierLabel,
    getPricingTierColor,
    getPricingTier,
    
    // Conference Info
    getConferenceName,
    getConferenceShortName,
    getConferenceTheme,
    getConferenceVenue,
    getContact,
    getSocial,
    getLogo,
    getDates,
    getDeadlines,
    getImportantDates,
    
    // Utility
    isRegistrationOpen,
    isAbstractSubmissionOpen,
    getDaysUntilConference,
  };

  return (
    <ConferenceContext.Provider value={value}>
      {children}
    </ConferenceContext.Provider>
  );
};

// Default fallback configuration
const getDefaultConfig = (shortName) => ({
  shortName: shortName,
  name: shortName,
  fullName: 'Civil Engineering Conference 2026',
  title: 'CCAI-2026',
  theme: 'Building the Future of Infrastructure',
  description: 'Join leading civil engineers, researchers, and industry professionals for three days of innovation, collaboration, and knowledge sharing.',
  registrationOpen: true,
  abstractSubmissionOpen: true,
  dates: {
    start: '2026-09-17T09:00:00',
    end: '2026-09-19T18:00:00',
    submission: '2026-06-15',
    notification: '2026-07-20',
    cameraReady: '2026-08-10',
    earlyBirdDeadline: '2026-07-31',
    standardDeadline: '2026-08-28',
    finalDeadline: '2026-09-14',
  },
  venue: {
    city: 'Paris',
    country: 'France',
    name: 'Conference Center'
  },
  contact: {},
  social: {},
  branding: {},
  pricingTier: {
    tier: 'earlyBird',
    label: 'Early Bird',
    color: 'green',
  },
  tracks: [
    { id: 'structural', name: 'Structural Engineering', color: '#0ea5e9' },
    { id: 'geotechnical', name: 'Geotechnical Engineering', color: '#8b5cf6' },
    { id: 'transportation', name: 'Transportation Engineering', color: '#f59e0b' },
    { id: 'water', name: 'Water Resources', color: '#06b6d4' },
    { id: 'construction', name: 'Construction Management', color: '#10b981' },
    { id: 'environmental', name: 'Environmental Engineering', color: '#84cc16' },
  ]
});

// Default important details for immediate rendering
const getDefaultImportantDetails = () => ({
  ConferenceTitle: 'CCAI-2026',
  ConferenceDates: 'September 17-19, 2026',
  ConferenceVenue: 'Paris, France',
  ShortName: 'CCAI2026',
  confName: 'CCAI-2026', // Add confName for compatibility
  abstract_submission_deadline: '',
  registration_opens: '',
  EarlyBird: '',
  mid_term: '',
  OnSpot: '',
});

// Fallback pricing configuration
const getFallbackPricing = (category) => ({
  registrationCategory: 'Standard',
  prices: {
    speaker: 649,
    poster: 499,
    delegate: 749,
    listener: 749,
    student: 349,
    exhibitor: 5000,
  },
});

// Cache helpers
const getCachedLoginDetails = () => {
  try {
    const cached = localStorage.getItem('conferenceLoginDetails');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const getCachedImportantDetails = () => {
  try {
    const cached = localStorage.getItem('conferenceImportantDetails');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const getCachedSiteConfig = () => {
  try {
    const cached = localStorage.getItem('conferenceSiteConfig');
    if (cached) {
      return JSON.parse(cached);
    }
    const defaultConfig = getDefaultConfig(getCurrentConferenceShortName());
    localStorage.setItem('conferenceSiteConfig', JSON.stringify(defaultConfig));
    return defaultConfig;
  } catch {
    return getDefaultConfig(getCurrentConferenceShortName());
  }
};
